import React, { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Alert,
  Skeleton,
  Button,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate, useParams } from 'react-router-dom';

import { useDeleteLessonMutation, useMoveLessonMutation } from '../lessonsApi';
import { useConfirm } from '../../../shared/components/ConfirmProvider';
import ClampedTextTooltip from '../../../shared/components/ClampedTextTooltip';
import {
  SortableList,
  type SortableListRenderProps,
} from '../../../shared/components/SortableList';
import type { ReorderPlacement } from '../../../shared/utils/reorder';

import { Lesson } from '@/shared/types/lesson';

interface LessonsNavProps {
  lessons: Lesson[];
  isLoading?: boolean;
  error?: any;
  canEdit?: boolean;
  onCreateLesson?: () => void;
  backTo?: { label: string; path: string };
}

const LessonsNav: React.FC<LessonsNavProps> = ({
  lessons,
  isLoading,
  error,
  canEdit = false,
  onCreateLesson,
  backTo,
}) => {
  const navigate = useNavigate();
  const {
    lessonId: activeLessonId,
    unitId,
    courseId,
  } = useParams<{
    lessonId?: string;
    unitId?: string;
    courseId?: string;
  }>();
  const confirm = useConfirm();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [deleteLesson] = useDeleteLessonMutation();
  const [moveLesson] = useMoveLessonMutation();

  const sorted = [...lessons].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const handleMoveLesson = async (lessonId: string, placement: ReorderPlacement) => {
    await moveLesson({ lessonId, unitId: unitId!, placement }).unwrap();
  };

  const navigateToLesson = (lesson: Lesson) => {
    if (unitId && courseId) {
      navigate(`/curriculum/courses/${courseId}/units/${unitId}/lessons/${lesson.id}`);
      return;
    }

    if (unitId) {
      navigate(`/curriculum/units/${unitId}/lessons/${lesson.id}`);
      return;
    }

    navigate(`/curriculum/lessons/${lesson.id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, lesson: Lesson) => {
    event.preventDefault();
    event.stopPropagation();
    setErrorMsg(null);
    setMenuAnchorEl(event.currentTarget);
    setSelectedLesson(lesson);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedLesson(null);
  };

  const handleDelete = async () => {
    if (!selectedLesson) return;
    const target = selectedLesson;
    handleMenuClose();

    const confirmed = await confirm({
      title: 'Delete Lesson',
      message: `Are you sure you want to delete "${target.title}"? This also deletes its resources. This action cannot be undone.`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await deleteLesson(target.id).unwrap();
      if (activeLessonId === target.id) {
        const parentPath =
          backTo?.path ??
          (unitId && courseId
            ? `/curriculum/courses/${courseId}/units/${unitId}`
            : unitId
              ? `/curriculum/units/${unitId}`
              : '/curriculum/courses');
        void navigate(parentPath, { replace: true });
      }
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'error' in err
          ? String((err as { error?: unknown }).error ?? 'Unknown error')
          : 'Please check your permissions and try again.';
      setErrorMsg(`Failed to delete "${target.title}": ${message}`);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Back link */}
      {backTo && (
        <ListItemButton
          onClick={() => navigate(backTo.path)}
          dense
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 0.25,
          }}
        >
          <ChevronLeftIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.25 }} />
          <Typography color="text.secondary" fontWeight={700} noWrap>
            {backTo.label}
          </Typography>
        </ListItemButton>
      )}

      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1,
        }}
      >
        <Typography color="text.secondary" sx={{ lineHeight: 1, fontWeight: 700 }}>
          Lessons
        </Typography>
        {canEdit && (
          <Tooltip title="New Lesson">
            <IconButton size="small" onClick={onCreateLesson} aria-label="Create lesson">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Error */}
      {errorMsg && (
        <Alert severity="error" sx={{ mx: 1, mb: 1, fontSize: '0.75rem' }}>
          {errorMsg}
        </Alert>
      )}

      {/* List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <Box sx={{ px: 1.5 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={36} sx={{ mb: 0.5, borderRadius: 1 }} />
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mx: 1 }}>
            Failed to load lessons.
          </Alert>
        ) : sorted.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              No lessons yet
            </Typography>
            {canEdit && onCreateLesson && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateLesson}
                fullWidth
                sx={{ mt: 1.5 }}
              >
                Create Lesson
              </Button>
            )}
          </Box>
        ) : (
          <SortableList
            items={sorted}
            canEdit={canEdit}
            onMove={handleMoveLesson}
            getItemLabel={(lesson) => `Reorder ${lesson.title || 'Untitled lesson'}`}
            reorderErrorMessage="Failed to reorder lesson. Please try again."
            renderContainer={(children) => (
              <List dense disablePadding sx={{ px: 0.5 }}>
                {children}
              </List>
            )}
            renderItem={(lesson, dragProps) => (
              <SortableLessonItem
                lesson={lesson}
                index={dragProps.index}
                isActive={lesson.id === activeLessonId}
                canEdit={canEdit}
                onNavigate={navigateToLesson}
                onMenuOpen={handleMenuOpen}
                dragProps={dragProps}
              />
            )}
          />
        )}
      </Box>

      {/* Add button pinned at bottom */}
      {canEdit && !isLoading && sorted.length > 0 && (
        <Box sx={{ p: 1.5, pt: 0.5 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateLesson}
            fullWidth
            variant="text"
            sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}
          >
            New Lesson
          </Button>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        slotProps={{ paper: { elevation: 2 } }}
      >
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main', gap: 1 }}>
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

interface SortableLessonItemProps {
  lesson: Lesson;
  index: number;
  isActive: boolean;
  canEdit: boolean;
  onNavigate: (lesson: Lesson) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, lesson: Lesson) => void;
  dragProps: SortableListRenderProps;
}

const SortableLessonItem: React.FC<SortableLessonItemProps> = ({
  lesson,
  index,
  isActive,
  canEdit,
  onNavigate,
  onMenuOpen,
  dragProps,
}) => {
  const lessonTitle = lesson.title || 'Untitled lesson';
  const { setNodeRef, style, dragHandle } = dragProps;

  return (
    <ListItemButton
      ref={setNodeRef}
      style={style}
      selected={isActive}
      onClick={() => onNavigate(lesson)}
      sx={{
        borderRadius: 1,
        mb: 0.25,
        pr: canEdit ? 5 : 1.5,
      }}
    >
      {dragHandle && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>{dragHandle}</Box>
      )}
      <ListItemText
        sx={{ my: 0, minWidth: 0 }}
        disableTypography
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ minWidth: 18, fontWeight: 700, color: 'text.disabled', flexShrink: 0 }}
            >
              {index + 1}
            </Typography>
            <ClampedTextTooltip
              text={lessonTitle}
              lineClamp={3}
              sx={{ fontWeight: isActive ? 600 : 400, lineHeight: 1.35 }}
            />
          </Box>
        }
      />
      {canEdit && (
        <ListItemSecondaryAction>
          <IconButton
            size="small"
            edge="end"
            onClick={(e) => onMenuOpen(e, lesson)}
            aria-label="Lesson options"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  );
};

export default LessonsNav;
